import React, { useState, useEffect, useCallback } from 'react';
import { T, FONTS, SHADOW } from '../../constants/theme.js';
import {
  fetchStaffMember, updateStaff, toggleStaff, resetStaffPassword,
  fetchStaffAuditLogs,
} from '../../services/api.js';
import { Card, Btn, Field, Input, Select, Badge, Avatar, Modal, Toast, Spinner, Tabs, SectionHeader } from '../../components/ui.jsx';

const ROLES = [
  { id:'ADMIN',          label:'Admin' },
  { id:'MANAGER',        label:'Manager' },
  { id:'FRONT_DESK',     label:'Front Desk' },
  { id:'HOUSEKEEPER',    label:'Housekeeper' },
  { id:'ACCOUNTANT',     label:'Accountant' },
  { id:'RESERVATIONIST', label:'Reservationist' },
  { id:'READONLY',       label:'Read-only' },
];
const ROLE_COLOR = {
  ADMIN:'#6B46C1', MANAGER:T.teal, FRONT_DESK:T.success,
  HOUSEKEEPER:T.warning, ACCOUNTANT:T.gold, RESERVATIONIST:T.navy, READONLY:T.muted,
};
const ACTION_ICONS = {
  CHECK_IN:'✅', CHECK_OUT:'🚪', CANCEL_RESERVATION:'❌', CREATE_STAFF:'👤',
  UPDATE_STAFF:'✏️', ACTIVATE_STAFF:'🟢', DEACTIVATE_STAFF:'🔴',
  CREATE_RESERVATION:'📋', CONFIRM_RESERVATION:'✔️', NO_SHOW:'🚫',
  VIEW_RESERVATION:'👁️', VIEW_FOLIO:'🧾', CREATE_CHARGE:'💳',
  CREATE_PAYMENT:'💵', UPDATE_ROOM:'🏨', UPDATE_HOUSEKEEPING:'🧹',
  DEFAULT:'📝',
};

export default function StaffDetail({ staffId, property, onBack, onNavigate, initialTab = 'profile' }) {
  const [staff,       setStaff]       = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [tab,         setTab]         = useState(initialTab);
  const [editMode,    setEditMode]    = useState(false);
  const [form,        setForm]        = useState({});
  const [saving,      setSaving]      = useState(false);
  const [logs,        setLogs]        = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [toast,       setToast]       = useState('');
  const [confirmAction, setConfirmAction] = useState(null);

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const load = useCallback(async () => {
    if (!staffId) return;
    setLoading(true);
    try {
      const data = await fetchStaffMember(staffId);
      setStaff(data);
      setForm({
        firstName: data.firstName || '',
        lastName:  data.lastName  || '',
        email:     data.email     || '',
        phone:     data.phone     || '',
        role:      data.role      || 'FRONT_DESK',
        shift:     data.shift     || 'morning',
        notes:     data.notes     || '',
      });
    } catch (e) {
      showToast(`❌ ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [staffId]);

  const loadLogs = useCallback(async () => {
    if (!staffId) return;
    setLogsLoading(true);
    try {
      const data = await fetchStaffAuditLogs(staffId);
      setLogs(Array.isArray(data) ? data : (data?.content || []));
    } catch (_) { setLogs([]); }
    finally { setLogsLoading(false); }
  }, [staffId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === 'logs') loadLogs(); }, [tab, loadLogs]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateStaff(staffId, form);
      showToast('✅ Staff updated');
      setEditMode(false);
      load();
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setSaving(false); }
  };

  const handleToggle = async () => {
    setConfirmAction(null);
    try {
      await toggleStaff(staffId);
      showToast(`✅ Staff ${staff?.active ? 'deactivated' : 'activated'}`);
      load();
    } catch (e) { showToast(`❌ ${e.message}`); }
  };

  const handleReset = async () => {
    setConfirmAction(null);
    try {
      await resetStaffPassword(staffId);
      showToast(`✅ Password reset — new credentials sent to ${staff?.email}`);
    } catch (e) { showToast(`❌ ${e.message}`); }
  };

  const TABS = [
    { id:'profile', label:'👤 Profile' },
    { id:'logs',    label:'📋 Activity Logs' },
  ];

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}>
      <Spinner size={40} />
    </div>
  );

  if (!staff) return (
    <div style={{ textAlign:'center', padding:60 }}>
      <p style={{ fontSize:32, marginBottom:12 }}>👤</p>
      <p style={{ color:T.muted }}>Staff member not found.</p>
      <Btn variant="secondary" onClick={onBack} style={{ marginTop:16 }}>← Back to Staff</Btn>
    </div>
  );

  const fullName = staff.fullName || `${staff.firstName} ${staff.lastName}`;

  return (
    <div className="fade-up">
      <SectionHeader
        title={fullName}
        subtitle={`${ROLES.find(r => r.id === staff.role)?.label || staff.role} · ${staff.shift || 'morning'} shift`}
        right={<Btn variant="secondary" size="sm" onClick={onBack}>← Back</Btn>}
      />

      {/* ── Hero card ── */}
      <Card style={{ marginBottom:20, padding:'20px 24px' }}>
        <div style={{ display:'flex', gap:20, alignItems:'center', flexWrap:'wrap' }}>
          <div style={{ width:64, height:64, borderRadius:'50%',
            background:`linear-gradient(135deg, ${ROLE_COLOR[staff.role] || T.teal}, ${T.gold})`,
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:22, fontWeight:800, color:'#fff', flexShrink:0 }}>
            {fullName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0,2)}
          </div>
          <div style={{ flex:1, minWidth:200 }}>
            <p style={{ fontSize:20, fontWeight:800, color:T.navy }}>{fullName}</p>
            <div style={{ display:'flex', gap:10, marginTop:6, flexWrap:'wrap', alignItems:'center' }}>
              <Badge label={ROLES.find(r => r.id === staff.role)?.label || staff.role}
                     color={ROLE_COLOR[staff.role] || T.muted} />
              <span style={{ fontSize:12, color:T.muted }}>📧 {staff.email}</span>
              {staff.phone && <span style={{ fontSize:12, color:T.muted }}>📞 {staff.phone}</span>}
              {staff.notes && <span style={{ fontSize:12, color:T.muted }}>📝 {staff.notes}</span>}
            </div>
          </div>

          {/* Status & Actions */}
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            <div style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px',
              background: staff.active !== false ? `${T.success}15` : `${T.muted}15`,
              borderRadius:99, border:`1px solid ${staff.active !== false ? T.success : T.muted}40` }}>
              <div style={{ width:8, height:8, borderRadius:'50%',
                background: staff.active !== false ? T.success : T.muted }} />
              <span style={{ fontSize:12, fontWeight:600,
                color: staff.active !== false ? T.success : T.muted }}>
                {staff.active !== false ? 'Active' : 'Inactive'}
              </span>
            </div>
            <Btn size="sm" variant="secondary" onClick={() => setEditMode(true)}>✏️ Edit Profile</Btn>
            <Btn size="sm" variant="secondary"
              onClick={() => setConfirmAction('reset')}>🔑 Reset Password</Btn>
            <Btn size="sm" variant={staff.active !== false ? 'secondary' : 'primary'}
              onClick={() => setConfirmAction('toggle')}>
              {staff.active !== false ? '⛔ Deactivate' : '✅ Activate'}
            </Btn>
          </div>
        </div>
      </Card>

      <Tabs tabs={TABS} active={tab} onSelect={setTab} />

      {/* ── Profile tab ── */}
      {tab === 'profile' && (
        <Card style={{ marginTop:12 }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:16 }}>
            {[
              { label:'First Name',   value:staff.firstName },
              { label:'Last Name',    value:staff.lastName  },
              { label:'Email',        value:staff.email     },
              { label:'Phone',        value:staff.phone || '—' },
              { label:'Role',         value:ROLES.find(r=>r.id===staff.role)?.label || staff.role },
              { label:'Shift',        value:staff.shift || '—', style:{ textTransform:'capitalize' } },
              { label:'Property',     value:property?.name || '—' },
              { label:'Department',   value:staff.notes || '—' },
              { label:'Created',      value:staff.createdAt ? new Date(staff.createdAt).toLocaleDateString() : '—' },
              { label:'Last Updated', value:staff.updatedAt ? new Date(staff.updatedAt).toLocaleDateString() : '—' },
            ].map(row => (
              <div key={row.label} style={{ padding:'12px 16px', background:T.surface, borderRadius:10,
                border:`1px solid ${T.border}` }}>
                <p style={{ fontSize:10, fontWeight:700, color:T.muted, letterSpacing:.8,
                  textTransform:'uppercase', marginBottom:4 }}>{row.label}</p>
                <p style={{ fontSize:13, fontWeight:600, color:T.navy, ...row.style }}>{row.value}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ── Activity Logs tab ── */}
      {tab === 'logs' && (
        <Card style={{ marginTop:12, padding:0, overflow:'hidden' }}>
          <div style={{ padding:'16px 20px', borderBottom:`1px solid ${T.border}`,
            display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <p style={{ fontWeight:700, color:T.navy, fontSize:14 }}>
              Activity Logs — <span style={{ color:T.muted, fontWeight:400 }}>{logs.length} entries</span>
            </p>
            <Btn size="sm" variant="ghost" onClick={loadLogs}>↻ Refresh</Btn>
          </div>

          {logsLoading ? (
            <div style={{ padding:40, textAlign:'center' }}><Spinner /></div>
          ) : logs.length === 0 ? (
            <div style={{ padding:'40px 24px', textAlign:'center' }}>
              <p style={{ fontSize:28, marginBottom:8 }}>📭</p>
              <p style={{ fontSize:13, color:T.muted, fontWeight:600 }}>No activity yet</p>
              <p style={{ fontSize:12, color:T.muted, marginTop:4 }}>
                Actions performed by this staff member will appear here automatically.
              </p>
            </div>
          ) : (
            <div style={{ maxHeight:540, overflowY:'auto' }}>
              {logs.map((entry, i) => {
                const icon = ACTION_ICONS[entry.action] || ACTION_ICONS.DEFAULT;
                const dt   = entry.performedAt ? new Date(entry.performedAt) : null;
                const dateStr = dt ? dt.toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' }) : '';
                const timeStr = dt ? dt.toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit' }) : '';
                return (
                  <div key={i} style={{ display:'flex', gap:12, padding:'12px 20px',
                    borderBottom:`1px solid ${T.border}`,
                    background: i % 2 === 0 ? '#fff' : T.surface }}>
                    <div style={{ width:34, height:34, borderRadius:8, background:`${T.teal}12`,
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:16, flexShrink:0 }}>
                      {icon}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                        <span style={{ fontSize:12, fontWeight:700, color:T.navy,
                          textTransform:'uppercase', letterSpacing:.3 }}>
                          {(entry.action || '').replace(/_/g, ' ')}
                        </span>
                        {entry.entityType && (
                          <span style={{ fontSize:10, color:T.muted, background:T.surface,
                            border:`1px solid ${T.border}`, borderRadius:99, padding:'1px 7px' }}>
                            {entry.entityType}
                          </span>
                        )}
                      </div>
                      {entry.details && (
                        <p style={{ fontSize:11, color:T.slate, marginTop:2, wordBreak:'break-all' }}>
                          {entry.details}
                        </p>
                      )}
                    </div>
                    <div style={{ flexShrink:0, textAlign:'right' }}>
                      <p style={{ fontSize:11, color:T.muted, fontWeight:500 }}>{timeStr}</p>
                      <p style={{ fontSize:10, color:T.muted }}>{dateStr}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {/* ── Edit Modal ── */}
      <Modal open={editMode} onClose={() => setEditMode(false)} title="Edit Staff Member" width={600}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
          <Field label="First Name" required><Input value={form.firstName} onChange={v => setForm(p=>({...p,firstName:v}))} /></Field>
          <Field label="Last Name"  required><Input value={form.lastName}  onChange={v => setForm(p=>({...p,lastName:v}))}  /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={v => setForm(p=>({...p,phone:v}))} /></Field>
          <Field label="Role" required>
            <Select value={form.role} onChange={v => setForm(p=>({...p,role:v}))}>
              {ROLES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
            </Select>
          </Field>
          <Field label="Shift">
            <Select value={form.shift} onChange={v => setForm(p=>({...p,shift:v}))}>
              {['morning','afternoon','evening','night','flexible'].map(s =>
                <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
            </Select>
          </Field>
          <Field label="Department / Notes">
            <Input value={form.notes} onChange={v => setForm(p=>({...p,notes:v}))} />
          </Field>
        </div>
        <div style={{ display:'flex', gap:10, marginTop:16 }}>
          <Btn variant="secondary" onClick={() => setEditMode(false)} style={{ flex:1 }}>Cancel</Btn>
          <Btn variant="primary" disabled={saving} onClick={handleSave} style={{ flex:2 }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </Btn>
        </div>
      </Modal>

      {/* ── Confirm Action Modal ── */}
      <Modal open={!!confirmAction} onClose={() => setConfirmAction(null)}
        title={confirmAction === 'reset' ? '🔑 Reset Password' : confirmAction === 'toggle'
          ? (staff.active !== false ? '⛔ Deactivate Staff' : '✅ Activate Staff') : ''} width={420}>
        <p style={{ fontSize:13, color:T.slate, lineHeight:1.6 }}>
          {confirmAction === 'reset'
            ? `A new temporary password will be generated and sent to ${staff?.email}. The current password will be invalidated immediately.`
            : confirmAction === 'toggle'
              ? (staff.active !== false
                ? `Deactivating ${fullName} will prevent them from logging in. You can reactivate at any time.`
                : `Activating ${fullName} will restore their login access.`)
              : ''}
        </p>
        <div style={{ display:'flex', gap:10, marginTop:20 }}>
          <Btn variant="secondary" onClick={() => setConfirmAction(null)} style={{ flex:1 }}>Cancel</Btn>
          <Btn variant={confirmAction === 'toggle' && staff.active !== false ? 'danger' : 'primary'}
            onClick={confirmAction === 'reset' ? handleReset : handleToggle} style={{ flex:2 }}>
            {confirmAction === 'reset' ? 'Reset Password'
              : (staff.active !== false ? 'Deactivate' : 'Activate')}
          </Btn>
        </div>
      </Modal>

      <Toast message={toast} />
    </div>
  );
}
